import { useRef, useState, type DragEvent, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, EmptyState, Field, Icon, IconButton, Input, Select, TextArea, useToast } from "@agentsaas/ui";
import {
  CapabilityId,
  CapabilityManifest,
  McpScope,
  type Capability,
  type CapabilityFile,
  type CapabilitySkill,
  type LocalizedText,
} from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { formatBytes } from "../app/format";
import { Loading } from "../layout/Loading";
import { NotFound } from "../pages/NotFound";
import { CodeEditor } from "../files/CodeEditor";
import { capk, useCapability, useMcpServers } from "../data/capabilities";
import { CapFrame, SkillFileTree, useCategoryLabel } from "./common";
import { CATEGORIES, fileSize, isText, manifestSize, MAX_TOTAL_BYTES, SKILL_NAME_RE, validPath } from "./model";
import { importFiles } from "./importer";

/**
 * 新建 / 编辑租户能力包（/t/:tid/capabilities/new[?from=] 与 /t/:tid/capabilities/:capid/edit）：
 * 表单编辑 manifest（中英名称描述、分类、图标、工具、运行环境、参数、instructions）；
 * skill 可上传文件夹或 zip（浏览器端解析成 files[]），也可在线新建 / 编辑单个文件；提交即发布新版本。
 */

const ICONS = ["sparkles", "image", "cube", "grid", "upload", "terminal", "code", "check", "tree", "mic", "pr", "file", "chart", "globe", "key", "plug", "tool", "brain"];
type ParamType = CapabilityManifest["params"][number]["type"];
const PARAM_TYPES: ParamType[] = ["string", "number", "boolean", "enum"];
/** 输入框里的示例（不是文案，不翻译） */
const EXAMPLE = { tools: "Bash, Edit, WebFetch", image: "agentsaas/agent-art:latest", egress: "api.example.com" };

interface ParamDraft {
  key: string;
  zh: string;
  en: string;
  type: ParamType;
  options: string;
  def: string;
}
interface McpDraft {
  name: string;
  serverId: string;
  allowTools: string;
}
interface Draft {
  id: string;
  name: LocalizedText;
  description: LocalizedText;
  category: string;
  icon: string;
  mcpScopes: McpScope[];
  allowedTools: string;
  externalMcp: McpDraft[];
  permissionRules: CapabilityManifest["tools"]["permissionRules"];
  image: string;
  egress: string;
  params: ParamDraft[];
  instructions: string;
  skills: CapabilitySkill[];
}

const splitList = (s: string) =>
  s
    .split(/[\n,]/)
    .map((x) => x.trim())
    .filter(Boolean);

function draftOf(c: Capability | undefined, copy: boolean): Draft {
  if (!c)
    return {
      id: "",
      name: {},
      description: {},
      category: "other",
      icon: "sparkles",
      mcpScopes: [],
      allowedTools: "",
      externalMcp: [],
      permissionRules: [],
      image: "",
      egress: "",
      params: [],
      instructions: "",
      skills: [],
    };
  const m = c.manifest;
  return {
    id: copy ? `${c.id}-custom`.slice(0, 48) : c.id,
    name: { ...m.name },
    description: { ...m.description },
    category: m.category,
    icon: m.icon,
    mcpScopes: [...m.tools.mcpScopes],
    allowedTools: m.tools.allowedTools.join(", "),
    // 复制内置能力包时不带外部 MCP 引用（服务属于租户）
    externalMcp: copy ? [] : m.tools.externalMcp.map((x) => ({ name: x.name, serverId: x.serverId, allowTools: x.allowTools.join(", ") })),
    permissionRules: m.tools.permissionRules,
    image: m.runtime.image ?? "",
    egress: m.runtime.egress.join("\n"),
    params: m.params.map((p) => ({ key: p.key, zh: p.label["zh-CN"] ?? "", en: p.label.en ?? "", type: p.type, options: (p.options ?? []).join(", "), def: p.default === undefined ? "" : String(p.default) })),
    instructions: m.instructions,
    skills: m.skills.map((s) => ({ name: s.name, files: s.files.map((f) => ({ ...f })) })),
  };
}

function cleanText(v: LocalizedText): LocalizedText | null {
  const out = Object.fromEntries(Object.entries(v).filter(([, s]) => s?.trim())) as LocalizedText;
  return Object.keys(out).length ? out : null;
}

function paramDefault(p: ParamDraft): string | number | boolean | undefined {
  if (p.def === "") return undefined;
  if (p.type === "number") return Number.isFinite(Number(p.def)) ? Number(p.def) : undefined;
  if (p.type === "boolean") return p.def === "true";
  return p.def;
}

/** 把草稿组装成 manifest；返回 zod 校验结果 */
export function buildManifest(d: Draft) {
  const name = cleanText(d.name) ?? {};
  return CapabilityManifest.safeParse({
    name,
    description: cleanText(d.description) ?? name,
    category: d.category,
    icon: d.icon,
    skills: d.skills,
    tools: {
      mcpScopes: d.mcpScopes,
      allowedTools: splitList(d.allowedTools),
      permissionRules: d.permissionRules,
      externalMcp: d.externalMcp.map((x) => ({ name: x.name.trim(), serverId: x.serverId, allowTools: splitList(x.allowTools) })),
    },
    runtime: { image: d.image.trim() || null, egress: splitList(d.egress) },
    params: d.params.map((p) => ({
      key: p.key.trim(),
      label: cleanText({ "zh-CN": p.zh, en: p.en }) ?? { en: p.key },
      type: p.type,
      ...(p.type === "enum" ? { options: splitList(p.options) } : {}),
      ...(paramDefault(p) !== undefined ? { default: paramDefault(p) } : {}),
    })),
    instructions: d.instructions,
  });
}

/** skill 草稿的问题（界面提示用） */
export function skillProblems(skills: CapabilitySkill[]): { skill: number; code: "name" | "dup" | "skill_md" | "path" | "empty" }[] {
  const out: { skill: number; code: "name" | "dup" | "skill_md" | "path" | "empty" }[] = [];
  skills.forEach((s, i) => {
    if (!SKILL_NAME_RE.test(s.name)) out.push({ skill: i, code: "name" });
    else if (skills.findIndex((x) => x.name === s.name) !== i) out.push({ skill: i, code: "dup" });
    if (!s.files.length) out.push({ skill: i, code: "empty" });
    else if (!s.files.some((f) => f.path === "SKILL.md")) out.push({ skill: i, code: "skill_md" });
    if (s.files.some((f) => !validPath(f.path))) out.push({ skill: i, code: "path" });
  });
  return out;
}

export function CapabilityEditorPage() {
  const { tid = "", capid } = useParams();
  const [sp] = useSearchParams();
  const from = capid ?? sp.get("from") ?? undefined;
  const base = useCapability(tid, from);
  if (from && base.isError) return <NotFound />;
  if (from && base.isPending)
    return (
      <CapFrame section="library">
        <Loading />
      </CapFrame>
    );
  // 只能编辑本租户的能力包；内置的只能复制
  if (capid && !base.data?.tenantId) return <NotFound />;
  return (
    <CapFrame section="library">
      <CapabilityEditor key={`${capid ?? "new"}:${from ?? ""}:${base.data?.version ?? 0}`} tid={tid} base={base.data} mode={capid ? "edit" : "create"} />
    </CapFrame>
  );
}

const SKILL_TEMPLATE = (name: string) => `---\nname: ${name}\ndescription: \n---\n\n# ${name}\n`;

function CapabilityEditor({ tid, base, mode }: { tid: string; base: Capability | undefined; mode: "create" | "edit" }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const catLabel = useCategoryLabel();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const servers = useMcpServers(tid);
  const [d, setD] = useState<Draft>(() => draftOf(base, mode === "create"));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const setText = (k: "name" | "description", l: "zh-CN" | "en", v: string) => setD((x) => ({ ...x, [k]: { ...x[k], [l]: v } }));
  const setSkills = (fn: (s: CapabilitySkill[]) => CapabilitySkill[]) => setD((x) => ({ ...x, skills: fn(x.skills) }));

  const idOk = mode === "edit" || CapabilityId.safeParse(d.id).success;
  const nameOk = !!cleanText(d.name);
  const problems = skillProblems(d.skills);
  const parsed = buildManifest(d);
  const size = manifestSize({ skills: d.skills } as CapabilityManifest);
  const tooBig = size > MAX_TOTAL_BYTES;
  const valid = idOk && nameOk && !problems.length && parsed.success && !tooBig;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !parsed.success) return;
    setBusy(true);
    setError(null);
    try {
      const c =
        mode === "edit"
          ? await api.call("capabilityUpdate", { params: { tid, capid: base!.id }, body: { manifest: parsed.data } })
          : await api.call("capabilityCreate", { params: { tid }, body: { id: d.id, manifest: parsed.data } });
      toast.show({ message: t("cap.published", { version: c.version }), tone: "success" });
      await qc.invalidateQueries({ queryKey: capk.all });
      navigate(`/t/${tid}/capabilities/${c.id}`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="cap-form" onSubmit={submit} noValidate data-testid="cap-form">
      <div className="page__head">
        <h1 className="page__title">{mode === "edit" ? t("cap.edit_title", { id: base!.id }) : t("cap.new")}</h1>
        <Link to={mode === "edit" ? `/t/${tid}/capabilities/${base!.id}` : `/t/${tid}/capabilities`}>{t("common.back")}</Link>
      </div>
      {mode === "edit" ? <p className="banner banner--warning small">{t("cap.publish_hint", { version: base!.version + 1 })}</p> : null}

      <section className="card" aria-labelledby="cap-sec-basic">
        <h2 className="section-title" id="cap-sec-basic">
          {t("cap.form.basic")}
        </h2>
        <Field label={t("cap.form.id")} hint={t("cap.form.id_hint")} error={touched && !idOk ? t("cap.form.id_invalid") : undefined}>
          {(id, desc) => <Input id={id} aria-describedby={desc} value={d.id} disabled={mode === "edit"} onChange={(e) => set("id", e.target.value.toLowerCase())} maxLength={48} data-testid="cap-id" />}
        </Field>
        <div className="cap-form__pair">
          <Field label={t("cap.form.name_zh")} error={touched && !nameOk ? t("cap.form.name_required") : undefined}>
            {(id) => <Input id={id} lang="zh-CN" value={d.name["zh-CN"] ?? ""} onChange={(e) => setText("name", "zh-CN", e.target.value)} data-testid="cap-name-zh" />}
          </Field>
          <Field label={t("cap.form.name_en")}>{(id) => <Input id={id} lang="en" value={d.name.en ?? ""} onChange={(e) => setText("name", "en", e.target.value)} data-testid="cap-name-en" />}</Field>
        </div>
        <div className="cap-form__pair">
          <Field label={t("cap.form.desc_zh")}>
            {(id) => <TextArea id={id} lang="zh-CN" rows={2} value={d.description["zh-CN"] ?? ""} onChange={(e) => setText("description", "zh-CN", e.target.value)} data-testid="cap-desc-zh" />}
          </Field>
          <Field label={t("cap.form.desc_en")}>
            {(id) => <TextArea id={id} lang="en" rows={2} value={d.description.en ?? ""} onChange={(e) => setText("description", "en", e.target.value)} data-testid="cap-desc-en" />}
          </Field>
        </div>
        <div className="cap-form__pair">
          <Field label={t("cap.category")}>
            {(id) => (
              <Select id={id} value={d.category} onChange={(e) => set("category", e.target.value)} data-testid="cap-form-category">
                {[...CATEGORIES, ...(CATEGORIES.includes(d.category as never) ? [] : [d.category])].map((c) => (
                  <option key={c} value={c}>
                    {catLabel(c)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t("cap.form.icon")}>
            {(id) => (
              <Select id={id} value={d.icon} onChange={(e) => set("icon", e.target.value)}>
                {[...ICONS, ...(ICONS.includes(d.icon) ? [] : [d.icon])].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </section>

      <SkillsSection skills={d.skills} setSkills={setSkills} touched={touched} problems={problems} size={size} tooBig={tooBig} />

      <section className="card" aria-labelledby="cap-sec-tools">
        <h2 className="section-title" id="cap-sec-tools">
          {t("cap.form.tools")}
        </h2>
        <fieldset className="tpl-scopes">
          <legend className="ui-field__label">{t("cap.tools.scopes")}</legend>
          <div className="tpl-scopes__grid">
            {McpScope.options.map((s) => (
              <label key={s} className="check">
                <input type="checkbox" checked={d.mcpScopes.includes(s)} onChange={(e) => set("mcpScopes", e.target.checked ? [...d.mcpScopes, s] : d.mcpScopes.filter((x) => x !== s))} data-testid={`cap-scope-${s}`} />
                <span>
                  <code>{s}</code> <span className="muted small">{t(`template.scope.${s}`)}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <Field label={t("cap.tools.allowed")} hint={t("cap.form.list_hint")}>
          {(id, desc) => <Input id={id} aria-describedby={desc} value={d.allowedTools} onChange={(e) => set("allowedTools", e.target.value)} placeholder={EXAMPLE.tools} data-testid="cap-allowed" />}
        </Field>
        <div className="stack">
          <div className="row">
            <span className="ui-field__label grow">{t("cap.tools.external")}</span>
            <Button
              size="sm"
              icon="plus"
              disabled={!servers.data?.length}
              onClick={() => set("externalMcp", [...d.externalMcp, { name: "", serverId: servers.data?.[0]?.id ?? "", allowTools: "*" }])}
              data-testid="cap-mcp-add"
            >
              {t("cap.form.mcp_add")}
            </Button>
          </div>
          {!servers.data?.length ? (
            <p className="muted small">
              {t("cap.form.mcp_none")} <Link to={`/t/${tid}/integrations/mcp`}>{t("cap.nav.mcp")}</Link>
            </p>
          ) : null}
          {d.externalMcp.map((x, i) => {
            const upd = (patch: Partial<McpDraft>) => set("externalMcp", d.externalMcp.map((y, j) => (j === i ? { ...y, ...patch } : y)));
            return (
              <div key={i} className="cap-rowedit">
                <Field label={t("cap.form.mcp_server")}>
                  {(id) => (
                    <Select id={id} value={x.serverId} onChange={(e) => upd({ serverId: e.target.value })}>
                      {(servers.data ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label={t("cap.form.mcp_name")} error={touched && !/^[a-z][a-z0-9_]{1,31}$/.test(x.name) ? t("cap.form.mcp_name_invalid") : undefined}>
                  {(id) => <Input id={id} value={x.name} onChange={(e) => upd({ name: e.target.value.toLowerCase() })} data-testid="cap-mcp-name" />}
                </Field>
                <Field label={t("cap.form.mcp_allow")}>{(id) => <Input id={id} value={x.allowTools} onChange={(e) => upd({ allowTools: e.target.value })} />}</Field>
                <IconButton icon="trash" label={t("cap.form.remove")} onClick={() => set("externalMcp", d.externalMcp.filter((_, j) => j !== i))} />
              </div>
            );
          })}
        </div>
      </section>

      <section className="card" aria-labelledby="cap-sec-runtime">
        <h2 className="section-title" id="cap-sec-runtime">
          {t("cap.form.runtime")}
        </h2>
        <Field label={t("cap.runtime.image")} hint={t("cap.form.image_hint")}>
          {(id, desc) => <Input id={id} aria-describedby={desc} value={d.image} onChange={(e) => set("image", e.target.value)} placeholder={EXAMPLE.image} data-testid="cap-image" />}
        </Field>
        <Field label={t("cap.runtime.egress")} hint={t("cap.form.egress_hint")}>
          {(id, desc) => <TextArea id={id} aria-describedby={desc} rows={2} className="mono" value={d.egress} onChange={(e) => set("egress", e.target.value)} placeholder={EXAMPLE.egress} />}
        </Field>
      </section>

      <section className="card" aria-labelledby="cap-sec-params">
        <div className="row">
          <h2 className="section-title grow" id="cap-sec-params">
            {t("cap.params")}
          </h2>
          <Button size="sm" icon="plus" onClick={() => set("params", [...d.params, { key: "", zh: "", en: "", type: "string", options: "", def: "" }])} data-testid="cap-param-add">
            {t("cap.form.param_add")}
          </Button>
        </div>
        {d.params.length ? null : <p className="muted small">{t("cap.form.params_hint")}</p>}
        {d.params.map((p, i) => {
          const upd = (patch: Partial<ParamDraft>) => set("params", d.params.map((y, j) => (j === i ? { ...y, ...patch } : y)));
          return (
            <div key={i} className="cap-rowedit" data-testid="cap-param">
              <Field label={t("cap.param.key")} error={touched && !/^[a-zA-Z][a-zA-Z0-9_]{0,31}$/.test(p.key) ? t("cap.form.param_key_invalid") : undefined}>
                {(id) => <Input id={id} value={p.key} onChange={(e) => upd({ key: e.target.value })} data-testid="cap-param-key" />}
              </Field>
              <Field label={t("cap.form.label_zh")}>{(id) => <Input id={id} lang="zh-CN" value={p.zh} onChange={(e) => upd({ zh: e.target.value })} />}</Field>
              <Field label={t("cap.form.label_en")}>{(id) => <Input id={id} lang="en" value={p.en} onChange={(e) => upd({ en: e.target.value })} data-testid="cap-param-en" />}</Field>
              <Field label={t("cap.param.type")}>
                {(id) => (
                  <Select id={id} value={p.type} onChange={(e) => upd({ type: e.target.value as ParamType })} data-testid="cap-param-type">
                    {PARAM_TYPES.map((x) => (
                      <option key={x} value={x}>
                        {t(`cap.param.type.${x}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              {p.type === "enum" ? <Field label={t("cap.form.options")}>{(id) => <Input id={id} value={p.options} onChange={(e) => upd({ options: e.target.value })} data-testid="cap-param-options" />}</Field> : null}
              <Field label={t("cap.param.default")}>
                {(id) =>
                  p.type === "boolean" ? (
                    <Select id={id} value={p.def} onChange={(e) => upd({ def: e.target.value })}>
                      <option value="">{t("cap.none")}</option>
                      <option value="true">{t("cap.form.yes")}</option>
                      <option value="false">{t("cap.form.no")}</option>
                    </Select>
                  ) : (
                    <Input id={id} type={p.type === "number" ? "number" : "text"} value={p.def} onChange={(e) => upd({ def: e.target.value })} data-testid="cap-param-default" />
                  )
                }
              </Field>
              <IconButton icon="trash" label={t("cap.form.remove")} onClick={() => set("params", d.params.filter((_, j) => j !== i))} />
            </div>
          );
        })}
      </section>

      <section className="card" aria-labelledby="cap-sec-instr">
        <h2 className="section-title" id="cap-sec-instr">
          {t("cap.instructions")}
        </h2>
        <Field label={t("cap.instructions")} hint={t("cap.form.instructions_hint")}>
          {(id, desc) => <TextArea id={id} aria-describedby={desc} rows={6} className="mono" value={d.instructions} onChange={(e) => set("instructions", e.target.value)} data-testid="cap-instructions" />}
        </Field>
      </section>

      {touched && !valid ? (
        <p className="form-error" role="alert" data-testid="cap-form-invalid">
          {t("cap.form.invalid")}
        </p>
      ) : null}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="row">
        <span className="muted small grow">{t("cap.form.size", { size: formatBytes(size, locale), max: formatBytes(MAX_TOTAL_BYTES, locale) })}</span>
        <Button type="submit" variant="primary" loading={busy} icon="upload" data-testid="cap-publish">
          {mode === "edit" ? t("cap.publish_version", { version: base!.version + 1 }) : t("cap.publish")}
        </Button>
      </div>
    </form>
  );
}

// ───────────── skill 文件 ─────────────

function SkillsSection({
  skills,
  setSkills,
  touched,
  problems,
  size,
  tooBig,
}: {
  skills: CapabilitySkill[];
  setSkills(fn: (s: CapabilitySkill[]) => CapabilitySkill[]): void;
  touched: boolean;
  problems: ReturnType<typeof skillProblems>;
  size: number;
  tooBig: boolean;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const folderRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const addImported = async (files: File[]) => {
    if (!files.length) return;
    try {
      const res = await importFiles(files);
      if (!res.skills.length) {
        toast.show({ message: t("cap.import.empty"), tone: "warning" });
        return;
      }
      setSkills((cur) => {
        const next = [...cur];
        for (const s of res.skills) {
          const i = next.findIndex((x) => x.name === s.name);
          if (i >= 0) next[i] = s;
          else next.push(s);
        }
        return next;
      });
      const files_ = res.skills.reduce((n, s) => n + s.files.length, 0);
      toast.show({ message: t("cap.import.done", { skills: res.skills.length, files: files_ }), tone: "success" });
      if (res.missingSkillMd.length) toast.show({ message: t("cap.import.no_skill_md", { name: res.missingSkillMd.join(", ") }), tone: "warning" });
      if (res.skipped.length) toast.show({ message: t("cap.import.skipped", { count: res.skipped.length }), tone: "warning" });
    } catch {
      toast.show({ message: t("cap.import.failed"), tone: "danger" });
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    void addImported([...e.dataTransfer.files]);
  };

  const addBlank = () =>
    setSkills((cur) => {
      let name = "new-skill";
      for (let n = 2; cur.some((s) => s.name === name); n++) name = `new-skill-${n}`;
      return [...cur, { name, files: [{ path: "SKILL.md", content: SKILL_TEMPLATE(name), encoding: "utf8", executable: false }] }];
    });

  return (
    <section className="card" aria-labelledby="cap-sec-skills">
      <div className="row">
        <h2 className="section-title grow" id="cap-sec-skills">
          {t("cap.skills")}
        </h2>
        <span className={`small ${tooBig ? "text-danger" : "muted"}`}>{formatBytes(size, locale)}</span>
      </div>
      <div
        className={`cap-drop${over ? " is-over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <Button size="sm" icon="folder" onClick={() => folderRef.current?.click()} data-testid="cap-upload-folder-btn">
          {t("cap.import.folder")}
        </Button>
        <Button size="sm" icon="upload" onClick={() => zipRef.current?.click()} data-testid="cap-upload-zip-btn">
          {t("cap.import.zip")}
        </Button>
        <Button size="sm" icon="plus" onClick={addBlank} data-testid="cap-skill-add">
          {t("cap.skill_add")}
        </Button>
        <span className="muted small">{t("cap.import.hint")}</span>
        <input
          ref={folderRef}
          type="file"
          multiple
          className="visually-hidden-input"
          tabIndex={-1}
          aria-hidden="true"
          {...{ webkitdirectory: "", directory: "" }}
          onChange={(e) => {
            void addImported([...(e.target.files ?? [])]);
            e.target.value = "";
          }}
          data-testid="cap-upload-folder"
        />
        <input
          ref={zipRef}
          type="file"
          accept=".zip,application/zip"
          className="visually-hidden-input"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            void addImported([...(e.target.files ?? [])]);
            e.target.value = "";
          }}
          data-testid="cap-upload-zip"
        />
      </div>
      {tooBig ? (
        <p className="form-error" role="alert">
          {t("cap.form.too_big")}
        </p>
      ) : null}
      {skills.length ? null : <EmptyState icon="file" title={t("cap.skills_empty")} />}
      {skills.map((s, i) => (
        <SkillEditor
          key={i}
          skill={s}
          problems={touched ? problems.filter((p) => p.skill === i).map((p) => p.code) : []}
          onChange={(next) => setSkills((cur) => cur.map((x, j) => (j === i ? next : x)))}
          onRemove={() => setSkills((cur) => cur.filter((_, j) => j !== i))}
        />
      ))}
    </section>
  );
}

function SkillEditor({ skill, problems, onChange, onRemove }: { skill: CapabilitySkill; problems: string[]; onChange(s: CapabilitySkill): void; onRemove(): void }) {
  const { t, locale } = useT();
  const [selected, setSelected] = useState<string | null>(skill.files.find((f) => f.path === "SKILL.md")?.path ?? skill.files[0]?.path ?? null);
  const [newPath, setNewPath] = useState("");
  const file = skill.files.find((f) => f.path === selected);
  const setFile = (path: string, patch: Partial<CapabilityFile>) => onChange({ ...skill, files: skill.files.map((f) => (f.path === path ? { ...f, ...patch } : f)) });
  const pathOk = validPath(newPath.trim()) && !skill.files.some((f) => f.path === newPath.trim());
  const addFile = () => {
    const p = newPath.trim();
    if (!pathOk) return;
    onChange({ ...skill, files: [...skill.files, { path: p, content: "", encoding: "utf8", executable: /\.(sh|py)$/.test(p) && p.startsWith("scripts/") }] });
    setSelected(p);
    setNewPath("");
  };
  return (
    <div className="cap-skill" data-testid="cap-skill" data-skill={skill.name}>
      <div className="cap-skill__head">
        <Icon name="folder" />
        <Input aria-label={t("cap.skill_name")} value={skill.name} onChange={(e) => onChange({ ...skill, name: e.target.value.toLowerCase() })} className="mono" data-testid="cap-skill-name" />
        <span className="muted small">{t("cap.files_count", { count: skill.files.length })}</span>
        <span className="grow" />
        <Button size="sm" variant="ghost" icon="trash" onClick={onRemove} data-testid="cap-skill-remove">
          {t("cap.skill_remove")}
        </Button>
      </div>
      {problems.map((p) => (
        <p key={p} className="form-error small" role="alert">
          {t(`cap.skill_problem.${p}`)}
        </p>
      ))}
      <div className="cap-skill__body">
        <div className="stack">
          <SkillFileTree files={skill.files} selected={selected} onSelect={setSelected} label={t("cap.files.tree", { skill: skill.name })} />
          <div className="row">
            <Input
              aria-label={t("cap.file_new")}
              placeholder={t("cap.file_new_placeholder")}
              value={newPath}
              onChange={(e) => setNewPath(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addFile();
                }
              }}
              className="grow mono"
              data-testid="cap-file-path"
            />
            <IconButton icon="plus" label={t("cap.file_add")} disabled={!pathOk} onClick={addFile} data-testid="cap-file-add" />
          </div>
        </div>
        <div className="cap-skill__editor">
          {file ? (
            <>
              <div className="row">
                <code className="grow ellipsis">{file.path}</code>
                <label className="check small">
                  <input type="checkbox" checked={file.executable} onChange={(e) => setFile(file.path, { executable: e.target.checked })} data-testid="cap-file-exec" />
                  <span>{t("cap.files.executable")}</span>
                </label>
                <span className="muted small">{formatBytes(fileSize(file), locale)}</span>
                <IconButton
                  icon="trash"
                  label={t("cap.file_delete", { path: file.path })}
                  onClick={() => {
                    onChange({ ...skill, files: skill.files.filter((f) => f.path !== file.path) });
                    setSelected(skill.files.find((f) => f.path !== file.path)?.path ?? null);
                  }}
                  data-testid="cap-file-delete"
                />
              </div>
              <div className="cap-editor-host">
                {isText(file) ? (
                  <CodeEditor path={`capedit/${skill.name}/${file.path}`} value={file.content} onChange={(v) => setFile(file.path, { content: v })} />
                ) : (
                  <EmptyState icon="file" title={t("cap.files.binary")}>
                    <Badge>{t("cap.files.binary_kept")}</Badge>
                  </EmptyState>
                )}
              </div>
            </>
          ) : (
            <EmptyState icon="file" title={t("cap.files.pick")} />
          )}
        </div>
      </div>
    </div>
  );
}
